import { Box, HStack, Skeleton, VStack } from '@chakra-ui/react';
import { useReceivedMothly } from '@/pages/Bookstore/services/hooks';
import { useMemo } from 'react';
import { FaFrown } from 'react-icons/fa';
import { SectionTitle } from '@/pages/Bookstore/components/atoms/Title';
import { useTheme } from '@/pages/Bookstore/contexts/BookstoreThemeAdapter';
import { BookCard } from '@/pages/Bookstore/components/molecules/BookCard';
import { SliderComponent } from '@/pages/Bookstore/components/molecules/HorizontalBooks';
import { Labeled } from '@/pages/Bookstore/components/molecules/Labeled';

type MonthlyHookResult = { data: any[] | undefined; isLoading: boolean };
type MonthlyHook = (params: Record<string, string>) => MonthlyHookResult;

export function MyBooksTemplate({
  description = '',
  filter,
  emptyLabel,
  hideEmpty = false,
  queryHook,
}: {
  description?: string;
  filter: Record<string, string>;
  emptyLabel?: string;
  hideEmpty?: boolean;
  queryHook?: MonthlyHook;
}) {
  const hookToUse = (queryHook ?? useReceivedMothly) as MonthlyHook;
  const { data: receivedBooks, isLoading } = hookToUse(filter);
  const { bookstoreLabels } = useTheme();
  const isMagazineList = filter.book__type === 'magazine';

  const sortedMonths = useMemo(() => {
    if (!receivedBooks) return receivedBooks;
    if (!isMagazineList) return receivedBooks;

    return receivedBooks.map((month) => ({
      ...month,
      books: month.books ?? [],
    }));
  }, [receivedBooks, isMagazineList]);

  const resultComponent = useMemo(
    () => (
      <VStack width={'100%'} paddingBottom={'100px'}>
        {sortedMonths?.length === 0 && !isLoading && !hideEmpty && (
          <VStack paddingTop={'50px'} spacing={3}>
            <FaFrown size="36px" color="#999" />
            <SectionTitle color="black">Você ainda não possui {emptyLabel || bookstoreLabels.prefixedPlural}</SectionTitle>
            <Box as="p" fontSize="14px" color="#888" textAlign="center" maxW="300px">
              Novas obras serão disponibilizadas para você em breve.
            </Box>
          </VStack>
        )}
        {isLoading && (
          <VStack width="100%" mt="15px" spacing={4}>
            {Array.from({ length: 2 }).map((_, i) => (
              <VStack key={i} width="100%" align="flex-start" paddingX="5%" spacing={2}>
                <Skeleton height="20px" width="200px" borderRadius="4px" />
                <HStack spacing={3} width="100%" overflow="hidden">
                  {Array.from({ length: 3 }).map((_, j) => (
                    <VStack
                      key={j}
                      flex="0 0 140px"
                      borderRadius="10px"
                      backgroundColor="white"
                      border="1px solid #ededed"
                      padding="8px"
                      boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
                    >
                      <Skeleton borderRadius="5px" width="100%" height="170px" />
                      <Skeleton height="12px" width="80%" borderRadius="4px" />
                      <Skeleton height="12px" width="60%" borderRadius="4px" />
                    </VStack>
                  ))}
                </HStack>
              </VStack>
            ))}
          </VStack>
        )}
        {description && (sortedMonths?.length ?? 0) > 0 && (
          <Labeled labelSize="24px" paddingX="5%" labelColor="rgba(0,0,0,0.6)" label={''} helpText={description} />
        )}
        {sortedMonths?.map((book) => (
          <VStack width="100%" key={`${book.label}`} mt="15px">
            <SliderComponent size={book.books.length} name={book.label} description={''}>
              {({ currentSlide }) =>
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                book.books?.map((b: any, index: number) => (
                  <BookCard
                    receivedBook
                    isActive={currentSlide === index}
                    key={b.book.id}
                    book={b.book}
                    deliveryCode={b.hashCode}
                    monthReference={b.monthReference}
                    yearReference={b.yearReference}
                  />
                ))
              }
            </SliderComponent>
          </VStack>
        ))}
      </VStack>
    ),
    [bookstoreLabels.prefixedPlural, description, emptyLabel, hideEmpty, isLoading, sortedMonths]
  );

  return resultComponent;
}
